<div class="card">
  <!-- Header with filters -->
  <div class="card-header border-bottom">
    <div class="d-flex flex-column flex-sm-row gap-3 align-items-start align-items-sm-center">
      <div class="d-flex align-items-center gap-2">
        <label class="text-nowrap fw-medium" for="statusFilter">{{ 'admin.applications.filterByStatus' | translate }}:</label>
        <select class="form-select form-select-sm" id="statusFilter" [(ngModel)]="statusFilter" (change)="onFilterChange()" style="width: auto;">
          <option value="">{{ 'admin.applications.allStatuses' | translate }}</option>
          <option value="pending">{{ 'admin.applications.statusPending' | translate }}</option>
          <option value="approved">{{ 'admin.applications.statusApproved' | translate }}</option>
          <option value="rejected">{{ 'admin.applications.statusRejected' | translate }}</option>
        </select>
      </div>
      <div class="text-muted text-nowrap ms-auto">
        <strong>{{ totalItems }}</strong> {{ 'admin.applications.totalCount' | translate | lowercase }}
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="card-body p-0">
    @if (loading) {
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    } @else if (applications.length === 0) {
      <div class="text-center py-5 text-muted">
        <i class="fa-solid fa-file-lines fa-3x mb-3 opacity-50"></i>
        <p class="mb-0">{{ 'admin.applications.noResults' | translate }}</p>
      </div>
    } @else {
      <!-- Table header -->
      <div class="table-header">
        <div>{{ 'admin.applications.applicant' | translate }}</div>
        <div>{{ 'admin.applications.description' | translate }}</div>
        <div>{{ 'admin.applications.status' | translate }}</div>
        <div>{{ 'admin.applications.submitted' | translate }}</div>
        <div>{{ 'admin.applications.actions' | translate }}</div>
      </div>

      <!-- Application list -->
      <div class="application-list">
        @for (app of applications; track app.application_id) {
          <div class="application-item" [class.expanded]="expandedId === app.application_id">
            <!-- Main row -->
            <div class="application-row" (click)="toggleExpand(app.application_id)">
              <div class="app-applicant">
                <i class="fa-solid fa-chevron-right expand-icon" [class.rotated]="expandedId === app.application_id"></i>
                <span class="fw-medium">{{ app.user_id }}</span>
              </div>
              <div class="app-description text-truncate">
                {{ app.trainer_description }}
              </div>
              <div class="app-status">
                <span class="badge" [ngClass]="getStatusBadgeClass(app.status)">{{ app.status }}</span>
              </div>
              <div class="app-date">
                {{ app.created_at | date:'dd.MM.yyyy' }}
              </div>
              <div class="app-actions" (click)="$event.stopPropagation()">
                @if (app.status === 'pending') {
                  <app-button variant="primary" size="sm" label="{{ 'admin.applications.approve' | translate }}" (clicked)="approveApplication(app)" [disabled]="actionLoading"></app-button>
                  <app-button variant="danger" size="sm" label="{{ 'admin.applications.reject' | translate }}" (clicked)="openRejectModal(app)" [disabled]="actionLoading"></app-button>
                } @else {
                  <span class="text-muted small">-</span>
                }
              </div>
            </div>

            <!-- Expanded details -->
            @if (expandedId === app.application_id) {
              <div class="application-details">
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.description' | translate }}</label>
                      <p>{{ app.trainer_description || '-' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.experience' | translate }}</label>
                      <p>{{ app.trainer_experience || '-' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.certificates' | translate }}</label>
                      <p>{{ app.trainer_certificates || '-' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.achievements' | translate }}</label>
                      <p>{{ app.trainer_achievements || '-' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.specializations' | translate }}</label>
                      <p>{{ app.trainer_specializations || '-' }}</p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.contactEmail' | translate }}</label>
                      <p>
                        @if (app.contact_email) {
                          <a [href]="'mailto:' + app.contact_email">{{ app.contact_email }}</a>
                        } @else {
                          -
                        }
                      </p>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="detail-group">
                      <label>{{ 'admin.applications.contactPhone' | translate }}</label>
                      <p>
                        @if (app.contact_phone) {
                          <a [href]="'tel:' + app.contact_phone">{{ app.contact_phone }}</a>
                        } @else {
                          -
                        }
                      </p>
                    </div>
                  </div>
                  @if (app.status === 'rejected' && app.rejection_reason) {
                    <div class="col-12">
                      <div class="detail-group">
                        <label class="text-danger">{{ 'admin.applications.rejectionReason' | translate }}</label>
                        <p class="text-danger">{{ app.rejection_reason }}</p>
                      </div>
                    </div>
                  }
                  @if (app.reviewed_at) {
                    <div class="col-12">
                      <div class="detail-group">
                        <label>{{ 'admin.applications.reviewedAt' | translate }}</label>
                        <p>{{ app.reviewed_at | date:'dd.MM.yyyy HH:mm' }}</p>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Pagination -->
      @if (totalPages > 1) {
        <div class="card-footer bg-light d-flex justify-content-between align-items-center">
          <span class="text-muted small">
            {{ 'admin.pagination.showing' | translate }} {{ (currentPage - 1) * perPage + 1 }}-{{ Math.min(currentPage * perPage, totalItems) }} {{ 'admin.pagination.of' | translate }} {{ totalItems }}
          </span>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="currentPage > 1 && onPageChange(currentPage - 1)">
                  <i class="fa-solid fa-chevron-left"></i>
                </a>
              </li>
              @for (page of getPages(); track page) {
                <li class="page-item" [class.active]="page === currentPage">
                  <a class="page-link" (click)="onPageChange(page)">{{ page }}</a>
                </li>
              }
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="currentPage < totalPages && onPageChange(currentPage + 1)">
                  <i class="fa-solid fa-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      }
    }
  </div>
</div>

<!-- Reject Modal -->
@if (showRejectModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ 'admin.applications.rejectTitle' | translate }}</h5>
          <button type="button" class="btn-close" (click)="closeRejectModal()"></button>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-3">{{ 'admin.applications.rejectDescription' | translate }}</p>
          <textarea class="form-control" rows="3"
                    [(ngModel)]="rejectionReason"
                    [placeholder]="'admin.applications.rejectionReasonPlaceholder' | translate"></textarea>
        </div>
        <div class="modal-footer">
          <app-button variant="neutral" label="{{ 'common.cancel' | translate }}" (clicked)="closeRejectModal()"></app-button>
          <app-button variant="danger" label="{{ 'admin.applications.confirmReject' | translate }}" (clicked)="confirmReject()" [disabled]="!rejectionReason.trim() || actionLoading"></app-button>
        </div>
      </div>
    </div>
  </div>
}
